import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Buildings, Money, ArrowsClockwise, TrendUp, TrendDown, Calendar, Briefcase, Student, ClipboardText, Warning } from "@phosphor-icons/react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, PieChart, Pie, Cell, Legend } from "recharts";
import api, { formatIDR, formatDate } from "../lib/api";
import { useAuth } from "../lib/auth";
import KaryawanDashboard from "./KaryawanDashboard";

const NOMEN_COLORS = ["#0B8D95", "#059669", "#10B981", "#34D399", "#6EE7B7", "#A7F3D0"];

function StatCard({ icon: Icon, label, value, sub, tone = "default", testid }) {
  const tones = {
    default: "text-gray-900",
    green: "text-[#059669]",
    red: "text-[#DC2626]",
    blue: "text-[#2563EB]",
    amber: "text-[#D97706]",
  };
  return (
    <div className="bg-white border border-gray-200 rounded-md p-5 card-lift" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-wider font-semibold text-gray-500">{label}</div>
        <div className="h-8 w-8 rounded-md bg-[#0B8D95]/8 text-[#0B8D95] flex items-center justify-center">
          <Icon size={16} weight="duotone" />
        </div>
      </div>
      <div className={`font-mono text-2xl xl:text-3xl font-bold ${tones[tone]}`}>{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [cashflow, setCashflow] = useState([]);
  const [saldo, setSaldo] = useState(null);
  const [agendaSummary, setAgendaSummary] = useState(null);
  const [lpjReminder, setLpjReminder] = useState(null);

  useEffect(() => {
    (async () => {
      const canFin = ["superadmin", "admin", "finance"].includes(user?.role);
      const [d, cf, sd] = await Promise.allSettled([
        api.get("/dashboard"),
        canFin ? api.get("/reports/cashflow?days=14") : Promise.reject(),
        canFin ? api.get("/reports/saldo") : Promise.reject(),
      ]);
      if (d.status === "fulfilled") setStats(d.value.data);
      if (cf.status === "fulfilled") setCashflow(cf.value.data);
      if (sd.status === "fulfilled") setSaldo(sd.value.data);
      if (user?.role === "superadmin") {
        try {
          const r = await api.get("/finance-agendas/summary");
          setAgendaSummary(r.data);
        } catch (_) { /* silent */ }
        try {
          const r2 = await api.get("/kegiatan/lpj-reminder");
          setLpjReminder(r2.data);
        } catch (_) { /* silent */ }
      }
    })();
  }, [user?.role]);

  const isKaryawan = user?.role === "karyawan";
  const showFin = ["superadmin", "admin", "finance"].includes(user?.role);

  if (isKaryawan) {
    return <KaryawanDashboard user={user} />;
  }

  const nomenPieData = saldo?.per_nomenklatur?.map((n, i) => ({
    name: n.code,
    value: n.income,
    fill: NOMEN_COLORS[i % NOMEN_COLORS.length],
  })) || [];

  return (
    <div data-testid="dashboard-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-[#0B8D95] font-semibold mb-2">Ringkasan Eksekutif</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black text-gray-900 tracking-tight">Dashboard Yayasan</h1>
        <p className="text-gray-600 mt-1">{showFin ? "Pantau performa keuangan, HR, dan operasional secara real-time." : "Pantau data karyawan, cuti, dan rekrutmen secara real-time."}</p>
      </div>

      {/* Stats — hero+cards fintech style */}
      {showFin && <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        {/* Big gradient hero card - like reference */}
        <div className="lg:col-span-1 rounded-2xl p-6 text-white relative overflow-hidden shadow-lg gradient-brand-radial" data-testid="hero-saldo">
          <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-white/10" />
          <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-white/5" />
          <div className="relative z-10">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/80 font-semibold mb-2">Total Saldo Real</div>
            <div className="font-mono text-3xl xl:text-4xl font-bold">{formatIDR(stats?.saldo || 0)}</div>
            <div className="text-xs text-white/80 mt-1">Pemasukan − Pengeluaran</div>
            <div className="grid grid-cols-2 gap-2 mt-5">
              <div className="rounded-xl bg-white/15 backdrop-blur p-3 border border-white/10">
                <div className="text-[9px] uppercase tracking-wider text-white/80 mb-1">Pemasukan Hari Ini</div>
                <div className="font-mono text-sm font-bold">{formatIDR(stats?.income_today || 0)}</div>
              </div>
              <div className="rounded-xl bg-white/15 backdrop-blur p-3 border border-white/10">
                <div className="text-[9px] uppercase tracking-wider text-white/80 mb-1">Bulan Ini</div>
                <div className="font-mono text-sm font-bold">{formatIDR(stats?.income_month || 0)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Right side stats */}
        <div className="lg:col-span-2 grid grid-cols-2 gap-4">
          <StatCard testid="stat-income-month" icon={TrendUp} label="Pemasukan Bulan Ini" value={formatIDR(stats?.income_month || 0)} tone="green" iconBg="bg-emerald-100 dark:bg-emerald-900/40" iconColor="text-emerald-600 dark:text-emerald-400" />
          <StatCard testid="stat-expense-month" icon={TrendDown} label="Pengeluaran Bulan Ini" value={formatIDR(stats?.expense_month || 0)} tone="red" iconBg="bg-rose-100 dark:bg-rose-900/40" iconColor="text-rose-600 dark:text-rose-400" />
          <StatCard testid="stat-lembaga" icon={Buildings} label="Lembaga" value={stats?.total_lembaga || 0} sub="Sekolah aktif" iconBg="bg-purple-100 dark:bg-purple-900/40" iconColor="text-purple-600 dark:text-purple-400" />
          <StatCard testid="stat-siswa" icon={Student} label="Siswa" value={stats?.total_siswa || 0} sub="Terdaftar" iconBg="bg-blue-100 dark:bg-blue-900/40" iconColor="text-blue-600 dark:text-blue-400" />
        </div>
      </div>}

      <div className={`grid grid-cols-2 ${showFin ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-4 mb-8`}>
        <StatCard testid="stat-employees" icon={Users} label="Karyawan" value={stats?.total_employees || 0} sub="Guru & Staff" iconBg="bg-cyan-100 dark:bg-cyan-900/40" iconColor="text-cyan-600 dark:text-cyan-400" />
        <StatCard testid="stat-open-rekrutmen" icon={Briefcase} label="Rekrutmen Aktif" value={stats?.open_rekrutmen || 0} sub="Pelamar aktif" tone="amber" iconBg="bg-amber-100 dark:bg-amber-900/40" iconColor="text-amber-600 dark:text-amber-400" />
        {showFin && <StatCard testid="stat-income-today" icon={Calendar} label="Pemasukan Hari Ini" value={formatIDR(stats?.income_today || 0)} tone="blue" iconBg="bg-blue-100 dark:bg-blue-900/40" iconColor="text-blue-600 dark:text-blue-400" />}
        <StatCard testid="stat-pending-leaves" icon={ArrowsClockwise} label="Cuti Pending" value={stats?.pending_leaves || 0} sub="Menunggu approval" iconBg="bg-orange-100 dark:bg-orange-900/40" iconColor="text-orange-600 dark:text-orange-400" />
      </div>

      {user?.role === "superadmin" && lpjReminder?.count > 0 && (
        <div
          className="mb-6 rounded-2xl border p-5 cursor-pointer transition-shadow hover:shadow-md bg-gradient-to-br from-rose-50 to-white border-rose-200 dark:from-rose-900/20 dark:to-transparent dark:border-rose-800"
          onClick={() => navigate("/kegiatan")}
          data-testid="lpj-reminder-card"
        >
          <div className="flex items-start gap-4">
            <div className="h-11 w-11 rounded-xl bg-rose-500/15 text-rose-700 dark:text-rose-300 flex items-center justify-center shrink-0">
              <ClipboardText size={22} weight="duotone" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <div className="font-display font-bold text-foreground">LPJ Belum Diupload</div>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-600 text-white flex items-center gap-1" data-testid="lpj-late-badge">
                  <Warning size={10} weight="fill" /> {lpjReminder.count} KEGIATAN
                </span>
              </div>
              <div className="text-sm text-muted-foreground">
                Kegiatan berikut sudah selesai lebih dari 3 hari tetapi LPJ belum diupload:
              </div>
              <ul className="mt-2 space-y-1">
                {lpjReminder.items.slice(0, 4).map((k) => (
                  <li key={k.id} className="text-xs text-foreground/80 flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />
                    <span className="font-semibold truncate">{k.title}</span>
                    <span className="text-muted-foreground">— {k.lembaga_name}</span>
                    <span className="ml-auto text-rose-600 font-bold">H+{k.days_late}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="text-xs text-rose-700 dark:text-rose-300 font-semibold hidden md:block">Kelola →</div>
          </div>
        </div>
      )}

      {user?.role === "superadmin" && agendaSummary && (agendaSummary.total > 0 || agendaSummary.overdue > 0 || agendaSummary.due_soon > 0) && (
        <div
          className="mb-6 rounded-2xl border p-5 cursor-pointer transition-shadow hover:shadow-md bg-gradient-to-br from-amber-50 to-white border-amber-200 dark:from-amber-900/20 dark:to-transparent dark:border-amber-800"
          onClick={() => navigate("/agenda-manager-keuangan")}
          data-testid="agenda-reminder-card"
        >
          <div className="flex items-start gap-4">
            <div className="h-11 w-11 rounded-xl bg-amber-500/15 text-amber-700 dark:text-amber-300 flex items-center justify-center shrink-0">
              <ClipboardText size={22} weight="duotone" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <div className="font-display font-bold text-foreground">Agenda Manager Keuangan</div>
                {agendaSummary.overdue > 0 && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-600 text-white flex items-center gap-1" data-testid="agenda-overdue-badge">
                    <Warning size={10} weight="fill" /> {agendaSummary.overdue} TERLAMBAT
                  </span>
                )}
                {agendaSummary.due_soon > 0 && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500 text-white" data-testid="agenda-duesoon-badge">
                    {agendaSummary.due_soon} DEADLINE ≤ 3 hari
                  </span>
                )}
              </div>
              <div className="text-sm text-muted-foreground">
                {agendaSummary.todo} to-do · {agendaSummary.in_progress} berjalan · {agendaSummary.done} selesai · total {agendaSummary.total}
              </div>
              {agendaSummary.upcoming?.length > 0 && (
                <ul className="mt-3 space-y-1.5">
                  {agendaSummary.upcoming.slice(0, 3).map((a) => (
                    <li key={a.id} className="text-xs text-foreground/80 flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                      <span className="font-semibold">{formatDate(a.agenda_date)}</span>
                      <span className="truncate">{a.title}</span>
                      {a.assignee_name && <span className="text-muted-foreground">— {a.assignee_name}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="text-xs text-amber-700 dark:text-amber-300 font-semibold hidden md:block">Kelola →</div>
          </div>
        </div>
      )}

      {showFin && <>
      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-md p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs uppercase tracking-wider font-semibold text-gray-500">Cashflow 14 Hari</div>
              <div className="font-display text-lg font-bold text-gray-900">Pemasukan vs Pengeluaran</div>
            </div>
            <div className="flex gap-3 text-xs">
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-[#059669]" />Pemasukan</span>
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-[#DC2626]" />Pengeluaran</span>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={cashflow}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
                <Tooltip formatter={(v) => formatIDR(v)} />
                <Line type="monotone" dataKey="income" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="expense" stroke="#DC2626" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-md p-5">
          <div className="text-xs uppercase tracking-wider font-semibold text-gray-500 mb-1">Distribusi Pemasukan</div>
          <div className="font-display text-lg font-bold text-gray-900 mb-3">Per Nomenklatur</div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={nomenPieData} dataKey="value" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={2}>
                  {nomenPieData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                </Pie>
                <Tooltip formatter={(v) => formatIDR(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Saldo per nomenklatur */}
      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider font-semibold text-gray-500">Rekap Saldo</div>
            <div className="font-display text-lg font-bold text-gray-900">Saldo per Nomenklatur</div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr className="text-xs uppercase tracking-wider text-gray-500">
                <th className="px-5 py-3 font-semibold">Kode</th>
                <th className="px-5 py-3 font-semibold">Nama Nomenklatur</th>
                <th className="px-5 py-3 font-semibold text-right">Pemasukan</th>
                <th className="px-5 py-3 font-semibold text-right">Pengeluaran</th>
                <th className="px-5 py-3 font-semibold text-right">Saldo</th>
              </tr>
            </thead>
            <tbody>
              {saldo?.per_nomenklatur?.map((n) => (
                <tr key={n.code} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-5 py-3 font-mono font-semibold text-[#0B8D95]">{n.code}</td>
                  <td className="px-5 py-3 text-gray-900">{n.name}</td>
                  <td className="px-5 py-3 text-right font-mono text-[#059669]">{formatIDR(n.income)}</td>
                  <td className="px-5 py-3 text-right font-mono text-[#DC2626]">{formatIDR(n.expense)}</td>
                  <td className={`px-5 py-3 text-right font-mono font-bold ${n.balance >= 0 ? "text-gray-900" : "text-[#DC2626]"}`}>{formatIDR(n.balance)}</td>
                </tr>
              ))}
              {!saldo?.per_nomenklatur?.length && (
                <tr><td colSpan={5} className="px-5 py-8 text-center text-gray-500">Belum ada data</td></tr>
              )}
            </tbody>
            {saldo?.per_nomenklatur?.length > 0 && (
              <tfoot className="bg-gray-50">
                <tr>
                  <td colSpan={2} className="px-5 py-3 font-semibold text-gray-900">Total</td>
                  <td className="px-5 py-3 text-right font-mono font-bold text-[#059669]">{formatIDR(saldo.total_income)}</td>
                  <td className="px-5 py-3 text-right font-mono font-bold text-[#DC2626]">{formatIDR(saldo.total_expense)}</td>
                  <td className="px-5 py-3 text-right font-mono font-bold text-gray-900">{formatIDR(saldo.saldo)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
      </>}
    </div>
  );
}
